import { useCallback, useState } from 'react';
import { Upload, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { uploadFileToGCS, verifyCOG } from './lib/upload-service';
import type { UploadResult } from './types';

export interface FileUploaderProps {
  label: string;
  accept: string;
  required?: boolean;
  onUploadComplete?: (result: UploadResult, file: File) => void;
  onError?: (error: string) => void;
  verifyCog?: boolean;
}

export function FileUploader({
  label,
  accept,
  required = false,
  onUploadComplete,
  onError,
  verifyCog = false,
}: FileUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [cogStatus, setCogStatus] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      setFile(selectedFile);
      setIsUploading(true);
      setCogStatus(null);
      setUploadComplete(false);

      try {
        const result = await uploadFileToGCS(selectedFile);
        
        // Verify COG if requested
        if (verifyCog) {
          const verification = await verifyCOG(result.gcsUrl);
          setCogStatus(verification.message);
        }

        setUploadComplete(true);
        onUploadComplete?.(result, selectedFile);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Upload failed';
        onError?.(errorMsg);
        console.error('File upload error:', err);
      } finally {
        setIsUploading(false);
      }
    },
    [onUploadComplete, onError, verifyCog]
  );

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          asChild
          className="relative cursor-pointer"
          disabled={isUploading}
        >
          <label>
            <Upload className="h-4 w-4 mr-2" />
            Choose File
            <input
              type="file"
              accept={accept}
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 cursor-pointer"
              disabled={isUploading}
            />
          </label>
        </Button>
        
        {isUploading && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading...
          </div>
        )}
        
        {!isUploading && uploadComplete && file && (
          <div className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle className="h-3 w-3" />
            {file.name}
          </div>
        )}
      </div>
      
      {cogStatus && (
        <div
          className={`text-xs flex items-center gap-1 ${
            cogStatus.includes('✓') ? 'text-green-600' : 'text-amber-600'
          }`}
        >
          {cogStatus.includes('✓') ? (
            <CheckCircle className="h-3 w-3" />
          ) : (
            <AlertCircle className="h-3 w-3" />
          )}
          {cogStatus}
        </div>
      )}
    </div>
  );
}
